import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, fleet, type CommandBatch,
         type FleetCommandRow } from "@/api/client";
import { ActionRow } from "@/components/ActionRow";
import { Chip, type ChipTone } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Donut } from "@/components/Donut";
import { Icon } from "@/components/icons";
import { CAP, useSession } from "@/auth/session";
import { POLL, ago } from "@/lib/format";
import { STATE_COLOR, STATE_MEANS, StateChip, levelLabel,
         commandLabel } from "@/modules/fleet/catalogue";

/** One run, as FOTA's `BatchCard` renders a rollout.
 *
 *  Same anatomy, deliberately: name and size on the header line, a status
 *  chip, the lifecycle buttons, a donut of the per-state counts, the same
 *  counts again as chips, and a Devices toggle that opens the per-device
 *  table. An operator who has watched a firmware rollout already knows how to
 *  read this.
 *
 *  POLL ONLY WHILE IT IS MOVING. FOTA refetches an `active` rollout at
 *  POLL.STATE and nothing otherwise, because a finished run's counts cannot
 *  change — and a page of finished runs each polling would be the cadence
 *  budget spent on nothing.
 */

const STATE_ORDER = [
  "pending", "sent", "received", "applied", "nacked",
  "superseded", "expired_undelivered", "expired_unapplied", "cancelled",
  // LAST, because it is the only ending that is about a person's decision
  // rather than the vehicle, and a run that has any is a run where somebody
  // chose to take a level away from an earlier command.
  "suspended",
];

const BATCH_CHIP: Record<string, ChipTone> = {
  draft: "mute", active: "info", done: "ok",
  cancelled: "bad", expired: "warn",
};

export function RunCard({ b }: { b: CommandBatch }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["fleet", "batch", b.batch_id],
    queryFn: () => fleet.batch(b.batch_id),
    enabled: open || b.status === "active",
    refetchInterval: b.status === "active" ? POLL.STATE : false,
  });
  const counts = detail.data?.counts ?? {};
  const hasCounts = Object.values(counts).some((n) => n > 0);

  const after = () => qc.invalidateQueries({ queryKey: ["fleet"] });
  //: WHICH ACT THE STEP-UP IS GATING, not a bare boolean. Two calls need the
  //: second factor now, and `onSuccess: () => start.mutate()` would have
  //: STARTED a run after the operator confirmed a DELETE -- the one bug this
  //: shape cannot be allowed to have, since start stops vehicles.
  const [stepUpFor, setStepUpFor] = useState<null | "start" | "delete">(null);
  const [code, setCode] = useState("");
  //: A SECOND CLICK, in the button itself. `window.confirm` is a modal the
  //: console uses nowhere else and that render tests cannot drive, and this
  //: act is irreversible AND destroys audit, so it does not get to be a
  //: one-click.
  const [confirmDel, setConfirmDel] = useState(false);
  const stepUp = useMutation({
    mutationFn: () => fleet.stepUp(code.trim()),
    onSuccess: () => {
      const act = stepUpFor;
      setStepUpFor(null);
      setCode("");
      if (act === "delete") del.mutate();
      else start.mutate();
    },
  });
  // STARTING IS WHAT STOPS VEHICLES, so the step-up lives here rather than on
  // the create. 428 is a PROMPT: branch on the STATUS, never on the message
  // text, which is the fallback `HTTP_428` when a body shape changes.
  const start = useMutation({
    mutationFn: () => fleet.startBatch(b.batch_id),
    onSuccess: after,
    onError: (e: unknown) => {
      if ((e as ApiError)?.status === 428) setStepUpFor("start");
    },
  });
  //: THE SAME 428 BRANCH AS `start`, on the STATUS and never the message
  //: text: the fallback is `HTTP_428` whenever a body shape changes.
  const del = useMutation({
    mutationFn: () => fleet.deleteBatch(b.batch_id),
    onSuccess: () => { setConfirmDel(false); after(); },
    onError: (e: unknown) => {
      if ((e as ApiError)?.status === 428) setStepUpFor("delete");
    },
  });
  const cancel = useMutation({ mutationFn: () => fleet.cancelBatch(b.batch_id),
                               onSuccess: after });
  const archive = useMutation({ mutationFn: () => fleet.archiveBatch(b.batch_id),
                                onSuccess: after });
  //: a step-up PROMPT is not a failure, so it must not also read as one
  //: TYPED, not left to the `??` chain. Each `useMutation` whose `onError`
  //: takes `unknown` infers TError as `unknown`, and `a ?? b` widens to
  //: `{} | unknown` -- which is not a `ReactNode`, so `{err && ...}` stopped
  //: compiling the moment a fourth mutation joined the chain. One cast here
  //: rather than four `onError` signatures changed.
  const err = (stepUpFor ? null
    : (start.error ?? cancel.error ?? archive.error ?? del.error)
  ) as Error | null;

  return (
    <div className="panel panel-pad" style={{ marginBottom: 12 }}>
      {/* TITLE, THEN `ActionRow` - not a hand-laid flex row with
          `marginLeft: auto` on the buttons. `tests/ui` refuses that, and the
          reason is in its own message: a CONVENTION is what produced 18 rows
          right and 18 left. FOTA's card predates the guard; new code does
          not get the exemption. */}
      <div style={{ marginBottom: 8 }}>
        <b style={{ color: "var(--t-bright)", fontSize: 13 }}>
          {b.name || commandLabel(b.action)}
        </b>
        {/* THE COMMAND ONLY WHEN THE NAME IS NOT ALREADY IT. An unnamed run
            falls back to the command for its title, and the meta line then
            printed the very same words again"Immobilise · Freewheel
            (throttle cut) Immobilise · Freewheel (throttle cut) · 1 asset". */}
        <span style={{ fontSize: 11.5, color: "var(--t-mute)", marginLeft: 8 }}>
          {b.name ? `${commandLabel(b.action)} · ` : ""}{b.commanded} asset
          {b.commanded === 1 ? "" : "s"}
          {b.level != null && ` · ${levelLabel(b.level)}`}
          {b.wave_size ? ` · wave ${b.wave_size}` : ""}
        </span>
        <ActionRow top={8}>
          <button className="btn ghost" onClick={() => setOpen((s) => !s)}>
            {open ? "▾" : "▸"} Assets
          </button>
          <Chip tone={BATCH_CHIP[b.status] ?? "mute"}>
            {b.status.toUpperCase()}
          </Chip>
          {b.status === "draft" && (
            <button className="btn teal" disabled={start.isPending}
              onClick={() => start.mutate()}>
              <Icon name="play" /> Start
            </button>
          )}
          {/* CANCEL, not Stop: final, no resume (D-16) - the same word FOTA
              chose for the same reason.
              OFFERED ON A DRAFT TOO. The server has always allowed it (it
              refuses only a run that has already finished), and a draft you
              have decided against is exactly the one you want to drop - the
              button was simply never rendered for it. */}
          {(b.status === "active" || b.status === "draft") && (
            <button className="btn red" disabled={cancel.isPending}
              onClick={() => cancel.mutate()}>
              <Icon name="stop" /> Cancel
            </button>
          )}
          {/* RED, like FOTA's. Archiving takes a run out of the active list - 
              something stops being available, which is exactly what red is
              reserved for in this console. */}
          {["done", "cancelled", "expired"].includes(b.status) && !b.archived_at && (
            <button className="btn red" disabled={archive.isPending}
              title="move out of the active list" onClick={() => archive.mutate()}>
              <Icon name="archive" /> Archive
            </button>
          )}
          {/* DELETE, ARCHIVED RUNS ONLY. Dattatray asked for it on the
              mobilise and immobilise runs, and archived-only is what keeps it
              a two-step act: a run must be terminal to be archived and
              archived to be deleted, so nothing live or merely finished is
              one mis-click from gone.

              THE SECOND CLICK SAYS WHAT IS LOST. "Delete" alone does not --
              this removes the command rows, which are the record of which
              vehicle was told what and by whom, so the label spells that out
              rather than asking "are you sure?" about an unnamed cost. */}
          {b.archived_at && (
            confirmDel ? (
              <>
                <button className="btn red" disabled={del.isPending}
                  title="permanently removes this run and its command rows"
                  onClick={() => del.mutate()}>
                  <Icon name="delete" /> Delete for ever
                </button>
                <button className="btn ghost" disabled={del.isPending}
                  onClick={() => setConfirmDel(false)}>Keep</button>
              </>
            ) : (
              /* RED ON THE FIRST CLICK TOO, which `tests/ui` insisted on
                  and was right about. I had made it `ghost` reasoning that
                  the first click only reveals the confirm and destroys
                  nothing -- but the convention is what carries the meaning:
                  if a button saying "Delete" under a bin icon can be grey,
                  then red stops reliably meaning "you lose something" and
                  every other red button in the console gets weaker.
                  A PLAIN JS COMMENT, not a braced JSX one: this sits in
                  the ternary's expression position rather than in JSX
                  children, and the braced form is a syntax error there.
                  tsc caught both that and the comment-terminator I then
                  wrote inside this very comment; `tests/ui` reads the file
                  as TEXT and passed on both broken versions. */
              <button className="btn red" onClick={() => setConfirmDel(true)}>
                <Icon name="delete" /> Delete
              </button>
            )
          )}
        </ActionRow>
      </div>

      {err && (
        <div style={{ fontSize: 11.5, color: "var(--bad)", marginBottom: 8 }}>
          {String((err as Error)?.message ?? "that did not work")}
        </div>
      )}

      {stepUpFor && (
        <div style={{ marginBottom: 10, padding: "10px 12px", borderRadius: 9,
              border: "1px solid var(--line)",
              borderLeft: "3px solid var(--warn)" }}>
          <b style={{ color: "var(--warn)" }}>Confirm it is you</b>
          <div style={{ fontSize: 12.5, marginTop: 4 }}>
            A code from your authenticator app, or a backup code. Valid five
            minutes. <b>Releasing never needs this.</b>
          </div>
          <ActionRow top={8}>
            <input className="inp mono" style={{ width: 170 }} value={code}
              autoComplete="one-time-code" placeholder="6-digit code"
              onChange={(e) => setCode(e.target.value)} />
            <button className="btn teal"
              disabled={!code.trim() || stepUp.isPending}
              onClick={() => stepUp.mutate()}>
              {/* NAMES THE ACT. "Confirm and start" over a delete would be a
                  button lying about which irreversible thing it does. */}
              {stepUpFor === "delete" ? "Confirm and delete" : "Confirm and start"}
            </button>
          </ActionRow>
          {stepUp.isError && (
            <div style={{ fontSize: 11.5, color: "var(--bad)" }}>
              That code was not accepted.
            </div>
          )}
        </div>
      )}

      {/* THE CHART ONLY WHEN THERE IS SOMETHING TO CHART. A collapsed
          archived run fetches no detail (same condition FOTA uses), so this
          rendered an empty donut saying "no data in scope" above a row saying
          "No device state yet" - two lines of filler on every archived card,
          which is what made the box taller than FOTA's. */}
      {hasCounts && (
        <>
          <Donut slices={STATE_ORDER
            .filter((st) => (counts[st] ?? 0) > 0)
            .map((st) => ({ label: st, value: counts[st] ?? 0,
                            color: STATE_COLOR[st] ?? "var(--t-mute)" }))} />

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {STATE_ORDER.filter((st) => (counts[st] ?? 0) > 0).map((st) => (
              <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                title={STATE_MEANS[st] ?? st}>
                <StateChip state={st} />
                <b style={{ fontSize: 11.5, color: "var(--t-bright)" }}>{counts[st]}</b>
              </span>
            ))}
          </div>
        </>
      )}
      {!hasCounts && b.status === "draft" && (
        <div style={{ fontSize: 12, color: "var(--t-mute)" }}>
          Nothing sent. This run is a draft.
        </div>
      )}

      {open && detail.data && (
        <div style={{ marginTop: 10 }}>
          <DataTable rows={detail.data.devices} cols={RUN_COLS}
            rowKey={(d) => d.cmd_id} maxHeight={420}
            empty={<div className="panel panel-pad cap">No assets in this run.</div>} />
        </div>
      )}
    </div>
  );
}

/** Module-const so DataTable's memoized rows re-render only the assets whose
 *  state actually moved — the same reason FOTA keeps its columns out here. */
const RUN_COLS: DataCol<FleetCommandRow & { asset_id: string | null }>[] = [
  { key: "asset", priority: 1, label: "Asset", width: 180, mono: true,
    title: (d) => d.asset_id ?? d.connection_id,
    render: (d) => d.asset_id ?? d.connection_id },
  { key: "state", priority: 1, label: "State", width: 210,
    title: (d) => STATE_MEANS[d.state] ?? d.state,
    render: (d) => (
      <>
        <StateChip state={d.state} />
        <div style={{ fontSize: 11, color: "var(--t-mute)", marginTop: 2,
                      whiteSpace: "normal", lineHeight: 1.25 }}>
          {STATE_MEANS[d.state] ?? "—"}
        </div>
      </>
    ) },
  { key: "att", priority: 3, label: "Attempts", width: 80, render: (d) => d.attempts ?? 0 },
  { key: "when", priority: 3, label: "Created", width: 90,
    render: (d) => (d.created_at ? `${ago(d.created_at)} ago` : "—") },
  // CANCEL LIVES HERE NOW. It used to sit in a fleet-wide command log, which
  // said Active/Archived a second time beside Active/Archived RUNS. One home:
  // the run that owns the command.
  { key: "act", priority: 1, label: "", width: 90, render: (d) => <CancelCommand row={d} /> },
];

/** Cancel one asset's command, inside the run that owns it.
 *
 *  OFFERED ONLY WHILE ORBIT STILL OWNS THE OUTCOME. A terminal command has
 *  nothing to call off, and one the device has already ACKed is ARMED on the
 *  vehicle, which only a MOBILISE can drop. A button that always fails would
 *  be worse than no button.
 */
function CancelCommand({ row }: { row: FleetCommandRow }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const cancel = useMutation({
    mutationFn: () => fleet.cancelCommand(row.cmd_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fleet"] }),
  });
  const terminal = ["applied", "nacked", "superseded", "expired_undelivered",
                    "expired_unapplied", "cancelled",
                    // ALREADY CLOSED. Offering "Cancel" on a suspended row
                    // would be a button that cannot do anything -- the server
                    // refuses a cancel on a terminal state -- and would imply
                    // Orbit still owns a command it has handed over.
                    "suspended"].includes(row.state);
  if (terminal || row.state === "received" || !can(CAP.IMMOBILIZE)) {
    return <span className="muted">—</span>;
  }
  return (
    <button type="button" className="btn ghost sm" disabled={cancel.isPending}
      onClick={() => cancel.mutate()}>Cancel</button>
  );
}
